import { getAdmByNumber, getCustomerByNumber } from "@/graphql/schemas/shared/utils/getCustomer"
import { getEmployeesByNumber } from "@/graphql/schemas/shared/utils/getEmployees"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"
import { AppSettings } from "@/settings"

import { getCartDiscountOption } from "../../cart/utils/getCart"
import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getArticleConfiguration } from "../../shared/utils/getArticleInformation"
import { getSanityOfficesAndArticles } from "../utils/orderHelpers"

export const queryCustomerOrderByNumber = {
  async customerOrderByNumber(_parent, args, context, _info) {
    const { orderNumber, cdn, lang } = args
    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.ORDERS,
      operation: Operations.READ,
    })

    const graphiQLQueryObject = [
      {
        method: "findOrder",
        variables: { orderNumber },
        fields: [
          "orderNumber",
          "customerNumber",
          "companyName1",
          "companyName2",
          "street",
          "country",
          "postalCode",
          "city",
          "additionalStreetInfo",
          "infoText1",
          "infoText2",
          "infoText3",
          "discount",
          "paymentMethod",
          "discountKey",
          "deliveryMethod",
          "deliveryMethodDescription",
          "shippingAddress1",
          "shippingAddress2",
          "shippingStreet",
          "shippingCountry",
          "shippingPostalCode",
          "shippingCity",
          "shippingAdditionalStreetInfo",
          "vatKey",
          "orderType",
          "orderNumber2",
          "orderDate",
          "shippingCosts",
          "packagingCosts",
          "otherCosts",
          "shippingDate",
          "customerOrderNumber",
          "customerOrderText",
          "costUnit",
          "admNumber",
          "employeeNumber",
          "totalAmount",
          "branchNumber",
          "customerBranchNumber",
          "orderStatus",
          "valueOfGoods",
          "discountAmount",
          "discountPercent",
          "grossInvoiceAmount",
          "netInvoiceAmount",
          "vatPercent",
          "vatAmount",
          "validTill",
          "valueDate",
          "picklist",
          "groupBillingKey",
        ],
        expandedFields: {
          items: [
            "posId",
            "sortNumber",
            "sku",
            "quantity",
            "contents",
            "container",
            "amount",
            "price",
            "description",
            "additionalDescription: description2",
            "discount",
            "totalPrice",
            "freightWeight",
            "totalFreightWeight",
            "itemStatus",
            "posId",
          ],
        },
      },
    ]

    const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
    const customerOrderResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data?.findOrder

    if (!customerOrderResponse) throw new Error("Customer order by number not found")

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customerOrderResponse.customerNumber,
    })

    const articleSkusIds = customerOrderResponse?.items.map((item) => item?.sku)
    const customerBranchNumber = customerOrderResponse?.customerBranchNumber
    const branchNumber = customerOrderResponse?.branchNumber
    const isRB = customerOrderResponse?.orderType === "RB"

    const [{ draftArticleSkus, customerBranch, branch, articles }, customerObject, admObject, employeeObject] =
      await Promise.all([
        getSanityOfficesAndArticles(
          {
            articleSkus: articleSkusIds,
            customerBranch: customerBranchNumber,
            branch: branchNumber,
          },
          client,
          isRB,
        ),
        getCustomerByNumber(customerOrderResponse?.customerNumber),
        getAdmByNumber(customerOrderResponse?.admNumber),
        getEmployeesByNumber(customerOrderResponse?.employeeNumber, client),
      ])

    const discountValue = getCartDiscountOption(
      customerObject?.custom?.fields?.discountKey?.id,
      customerObject?.custom?.fields?.discountKey?.obj,
      customerOrderResponse?.grossInvoiceAmount * 100,
    )

    let totalFreightWeight = 0
    const orderItems = customerOrderResponse?.items?.map(async (item) => {
      if (!item) return null
      let articleConfig = null

      const article = articles?.find((article) => article?.sku === item.sku)

      articleConfig = await getArticleConfiguration({ sku: item?.sku })
      totalFreightWeight = totalFreightWeight + Number(item?.totalFreightWeight)

      return {
        article,
        sortNumber: item?.sortNumber,
        totalFreightWeight: item?.totalFreightWeight,
        totalPrice: item?.totalPrice,
        quantity: item?.quantity,
        price: item?.price,
        itemStatus: item?.itemStatus,
        freightWeight: item?.freightWeight,
        amount: item?.amount,
        discount: item?.discount,
        description: item?.description,
        additionalDescription: item?.additionalDescription,
        posId: item?.posId,
        sellableInDecimals: articleConfig?.sellableInDecimals || false,
        positionId: item?.posId,
        container: item?.container,
      }
    })

    const draftArticleItems = isRB
      ? customerOrderResponse?.items?.filter((item) => draftArticleSkus?.includes(item?.sku))
      : []

    return {
      ...customerOrderResponse,
      items: await Promise.all(orderItems),
      draftItems: draftArticleItems,
      orderNumberLong: customerOrderResponse?.orderNumber,
      orderNumberShort: customerOrderResponse?.orderNumber2,
      paymentTill: customerOrderResponse?.valueDate,
      customer: customerObject,
      admUser: admObject,
      employeeUser: employeeObject,
      totalFreightWeight,
      customerBranch,
      branch,
      sokoDiscountValue: discountValue?.discount,
      sokoDiscountPercent: discountValue?.percent,
      sokoPrice: discountValue?.sokoTotal,
    }
  },
}
